import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";
import classroomStudents from "src/assets/classroom-students.jpg";
import classroomTeacher from "src/assets/classroom-teacher.jpg";
import WaitlistCTA from "./WaitlistCTA.js";

const Stories = () => {
  const theme = useTheme();

  const QuoteCell = ({ tall, wide, children }) => (
    <Box
      sx={{
        gridColumn: wide ? { xs: "auto", md: "span 2" } : "auto",
        gridRow: tall ? { xs: "auto", md: "span 2" } : "auto",
        bgcolor: "#1a1a1a",
        border: "1px solid rgba(255,255,255,0.08)",
        color: theme.palette.paper.primary,
        borderRadius: 2.5,
        p: 3.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );

  const QuoteMark = () => (
    <Box
      sx={{
        fontSize: 44,
        color: theme.palette.success.main,
        lineHeight: 0.7,
        fontWeight: 700,
      }}
    >
      "
    </Box>
  );

  const Attribution = ({ initials, name, role }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mt: 1.5,
        pt: 2,
        borderTop: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: theme.palette.success.main,
          color: theme.palette.paper.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.8125rem",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: theme.palette.paper.primary,
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}
        >
          {role}
        </Typography>
      </Box>
    </Box>
  );

  const Multiplier = ({ children }) => (
    <Box
      sx={{
        display: "inline-block",
        fontSize: 11,
        px: 1.25,
        py: 0.5,
        bgcolor: "#dcfce7",
        color: "#14532d",
        borderRadius: 999,
        fontWeight: 700,
        letterSpacing: "0.05em",
        alignSelf: "flex-start",
      }}
    >
      {children}
    </Box>
  );

  const PerfCell = ({ num, label, sub }) => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, #14532d 100%)`,
        color: theme.palette.paper.primary,
        borderRadius: 2.5,
        p: 3.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Typography
        sx={{
          fontSize: "3.5rem",
          fontWeight: 800,
          letterSpacing: "-0.03em",
          color: theme.palette.paper.primary,
          lineHeight: 1,
        }}
      >
        {num}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.6)",
          mt: 1.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "rgba(255,255,255,0.7)",
          mt: 1.5,
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {sub}
      </Typography>
      <Box
        component={RouterLink}
        to={getPageRoute("home", "REPORT")}
        sx={{
          mt: 2,
          pt: 2,
          borderTop: "1px solid rgba(255,255,255,0.15)",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: "0.9375rem",
          fontWeight: 700,
          color: theme.palette.paper.primary,
          textDecoration: "none",
          "&:hover .arrow": { transform: "translateX(4px)" },
        }}
      >
        Get the report{" "}
        <Box
          component="span"
          className="arrow"
          sx={{ transition: "transform 0.15s" }}
        >
          →
        </Box>
      </Box>
    </Box>
  );

  /**
   * Year 1 report CTA cell. Fills the last grid slot with a gated
   * download link, matching the dark cell aesthetic used elsewhere in
   * the grid.
   */
  const ReportCell = () => (
    <Box
      sx={{
        bgcolor: "#1a1a1a",
        border: `1px solid ${theme.palette.success.main}`,
        borderRadius: 2.5,
        p: 3.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        color: theme.palette.paper.primary,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -60,
          right: -60,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(32,156,51,0.25) 0%, transparent 70%)",
        }}
      />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 1.25,
          }}
        >
          Year 1 report · 2025-26
        </Typography>
        <Typography
          sx={{
            fontSize: "1.375rem",
            fontWeight: 800,
            letterSpacing: "-0.01em",
            lineHeight: 1.4,
            textWrap: "balance",
            color: theme.palette.paper.primary,
            mt: 0.5,
            mb: 0.5,
          }}
        >
          Every classroom. Every cohort. The methodology.
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: "rgba(255,255,255,0.65)",
            mt: 1.25,
            lineHeight: 1.5,
          }}
        >
          The 28-classroom breakdown, cohort-level Conditional Growth
          Percentiles, and what changed in each teacher's workflow.
        </Typography>
        <Box
          component={RouterLink}
          to={getPageRoute("home", "REPORT")}
          sx={{
            mt: 2.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: "0.9375rem",
            fontWeight: 700,
            color: theme.palette.success.main,
            textDecoration: "none",
            "&:hover .arrow": { transform: "translateX(4px)" },
          }}
        >
          Get the report{" "}
          <Box
            component="span"
            className="arrow"
            sx={{ transition: "transform 0.15s" }}
          >
            →
          </Box>
        </Box>
      </Box>
    </Box>
  );

  /**
   * Real classroom photo cell. Photo fills the cell as background image and
   * a bottom gradient overlay carries the caption. `wide` / `tall` mirror
   * QuoteCell so this can span rows/cols in the masonry.
   */
  const PhotoCell = ({ src, caption, sub, tall, wide }) => (
    <Box
      sx={{
        gridColumn: wide ? { xs: "auto", md: "span 2" } : "auto",
        gridRow: tall ? { xs: "auto", md: "span 2" } : "auto",
        borderRadius: 2.5,
        overflow: "hidden",
        position: "relative",
        minHeight: 230,
        backgroundImage: `url(${src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: theme.palette.paper.primary,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.8) 100%)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          p: 3.5,
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
          {caption}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", opacity: 0.85, mt: 0.5 }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="section"
      id="stories"
      sx={{
        py: { xs: 8, md: 14 },
        bgcolor: theme.palette.text.primary,
        color: theme.palette.paper.primary,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          Stories from the classroom
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.paper.primary,
          }}
        >
          Real teachers. Real classrooms.{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            Real growth
          </Box>
          .
        </Typography>
        <Typography
          sx={{
            fontSize: "1.25rem",
            color: "rgba(255,255,255,0.7)",
            mt: 2.75,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Every Maestro classroom beat the national growth norm in math and
          science in year one. Here's what that looked like in their words.
        </Typography>

        <Box
          sx={{
            mt: 8,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
            gridAutoRows: "minmax(230px, auto)",
            gap: 2,
          }}
        >
          <QuoteCell tall>
            <QuoteMark />
            <Typography
              sx={{ fontSize: { xs: "1.0625rem", md: "1.1875rem" }, lineHeight: 1.5, flex: 1, letterSpacing: "-0.005em" }}
            >
              I feel so much more <strong>confident</strong> with my materials
              now. When you have a good lesson planning process, you walk in
              thinking <strong>'I got this.'</strong> I don't have to worry
              anymore.
            </Typography>
            <Multiplier>2× improvement</Multiplier>
            <Attribution
              initials="KE"
              name="Katherine Escobar"
              role="7th Grade Science · KIPP Houston"
            />
          </QuoteCell>

          <PerfCell
            num="~317%"
            label="Science growth vs NWEA typical"
            sub="17 of 17 classes cleared typical growth. Median CGP 67-79th across every starting cohort."
          />

          <QuoteCell>
            <QuoteMark />
            <Typography
              sx={{ fontSize: { xs: "1.0625rem", md: "1.1875rem" }, lineHeight: 1.5, flex: 1, letterSpacing: "-0.005em" }}
            >
              Force and light are <strong>extremely tricky</strong> concepts for
              fifth graders. Compared to last year, my kids did{" "}
              <strong>significantly better</strong>.
            </Typography>
            <Multiplier>5.3× improvement</Multiplier>
            <Attribution
              initials="KF"
              name="Katie Foster"
              role="5th Grade Science · KIPP Houston"
            />
          </QuoteCell>

          <PhotoCell
            src={classroomStudents}
            caption="Collaborative project work"
            sub="6th Grade Science · KIPP Houston"
          />

          <QuoteCell>
            <QuoteMark />
            <Typography
              sx={{ fontSize: { xs: "1.0625rem", md: "1.1875rem" }, lineHeight: 1.5, flex: 1, letterSpacing: "-0.005em" }}
            >
              I was like <em>wow</em>, this made my life{" "}
              <strong>10 times easier</strong>. It's stuff that gets you
              excited. Like, oh man, <strong>this could really work</strong>.
            </Typography>
            <Multiplier>1.5× improvement</Multiplier>
            <Attribution
              initials="KR"
              name="Kyle Reiss"
              role="5th Grade Science · KIPP Houston"
            />
          </QuoteCell>

          <PerfCell
            num="~230%"
            label="Math growth vs i-Ready typical"
            sub="11 of 11 classes cleared typical growth. +33 pp avg shift at/above grade level."
          />

          <QuoteCell wide>
            <QuoteMark />
            <Typography
              sx={{ fontSize: { xs: "1.0625rem", md: "1.1875rem" }, lineHeight: 1.5, flex: 1, letterSpacing: "-0.005em" }}
            >
              My skill in January barely looks like the one I started with. We
              added Gallery Walks, simulation scaffolding, Spanish for my ELL
              students. Each one came out of a coach call. The classroom drove
              the change.
            </Typography>
            <Attribution
              initials="DG"
              name="Delonte Gardner"
              role="7th Grade Math · KIPP Houston · 12 skill updates since December"
            />
          </QuoteCell>

          <QuoteCell>
            <QuoteMark />
            <Typography
              sx={{ fontSize: { xs: "1.0625rem", md: "1.1875rem" }, lineHeight: 1.5, flex: 1, letterSpacing: "-0.005em" }}
            >
              The review was so detailed, they were{" "}
              <strong>knocking it out the park</strong>. The students{" "}
              <strong>loved</strong> that study guide.
            </Typography>
            <Multiplier>1.8× improvement</Multiplier>
            <Attribution
              initials="AL"
              name="Alexia Lewis"
              role="8th Grade Algebra I · KIPP Houston"
            />
          </QuoteCell>

          <ReportCell />

          <PhotoCell
            src={classroomTeacher}
            caption="Inside the classroom"
            sub="KIPP Houston · Year one"
          />
        </Box>

        <WaitlistCTA variant="dark" />
      </Container>
    </Box>
  );
};

export default Stories;
